import type { OTCDealData } from '@src/types/OTC';
import { isOTCForwardProductTemplate, useSecuritiesContext } from '@talos/kyoko';
import { useMemo } from 'react';

type NotionalCurrencies = {
  unitNotionalCurrency: string | undefined;
  forwardPriceCurrency: string | undefined;
};

/**
 * Core logic for calculating currencies based on payoff type
 */
const calculateCurrencies = (
  payoffType?: string | null,
  baseAsset?: string | null,
  quoteAsset?: string | null
): NotionalCurrencies => {
  if (!payoffType || !baseAsset || !quoteAsset) {
    return { unitNotionalCurrency: undefined, forwardPriceCurrency: undefined };
  }

  // Determine unit notional currency based on payoff type
  const unitNotionalCurrency = payoffType === 'Linear' ? baseAsset : quoteAsset;

  // Forward price always uses quote asset
  const forwardPriceCurrency = quoteAsset;

  return {
    unitNotionalCurrency,
    forwardPriceCurrency,
  };
};

/**
 * Hook to get the proper currencies for notional amount and forward price
 * based on the product details from the deal's symbol
 */
export const useForwardNotionalCurrencies = (deal: OTCDealData): NotionalCurrencies => {
  const { securitiesBySymbol } = useSecuritiesContext();

  return useMemo(() => {
    const symbol = deal.Symbol;
    if (!symbol) {
      return { unitNotionalCurrency: undefined, forwardPriceCurrency: undefined };
    }

    const security = securitiesBySymbol.get(symbol);
    if (!security || !isOTCForwardProductTemplate(security)) {
      return { unitNotionalCurrency: undefined, forwardPriceCurrency: undefined };
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return { unitNotionalCurrency: undefined, forwardPriceCurrency: undefined };
    }

    const { PayoffType, BaseAsset, QuoteAsset } = productDetails;
    return calculateCurrencies(PayoffType, BaseAsset, QuoteAsset);
  }, [deal, securitiesBySymbol]);
};

/**
 * Utility function to get currencies from product details directly
 * (for use cases where we don't have a full deal object)
 */
export const getNotionalCurrenciesFromProductDetails = (
  payoffType?: string | null,
  baseAsset?: string | null,
  quoteAsset?: string | null
): NotionalCurrencies => {
  return calculateCurrencies(payoffType, baseAsset, quoteAsset);
};
